// Oportunidade: o valor ORÇADO ao paciente (VE) — puro.
//
// É o quarto valor do funil, e o único que vem ANTES da venda:
//
//   valorOrcado   — o que foi PROPOSTO (a oportunidade). Nasce no comparecimento,
//                   ou depois: o orçamento sai da avaliação, e quem marca
//                   "compareceu" às vezes ainda não sabe. Por isso é opcional.
//   valorFechado  — o que virou contrato (a venda).
//   valorEntrada  — o pago no ato (caixa do dia).
//   valorRecebido — a soma dos recebimentos (base da comissão).
//
// Daqui saem as duas perguntas que a clínica não conseguia responder: quanto do
// que se orça vira contrato, e quanto dinheiro saiu pela porta.

import { ETAPAS_POS_COMPARECIMENTO } from '@/leads/cadencia';
import { importadoSemAtividade } from '@/leads/regras';

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const numero = (v) => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

export const valorOrcadoDe = (lead) => Math.max(0, numero(lead?.valorOrcado));

/**
 * ★★ WH — o lead foi DECLARADO "compareceu sem orçamento".
 *
 * ★ A marca é GRAVADA, e não derivada de `valorOrcado === 0`, porque esse zero é
 * **ambíguo**: quer dizer "não houve orçamento" **ou** "ninguém preencheu". Medido
 * em 28/set: **222 dos 228** leads em `compareceu` estão em zero. Sem a marca, as
 * duas situações são o mesmo dado — e separá-las é justamente o que o dono pediu.
 *
 * Só `=== true` conta: valor malformado não declara nada.
 */
export const semOrcamentoDeclarado = (lead) => lead?.semOrcamento === true;

/**
 * ★ VG — DESFECHO NEGATIVO é um MOTIVO QUE ENCERRA, não uma etapa.
 *
 * "Perdido" deixou de existir como coluna: as colunas de "não" são vivas, e o
 * que diz que o lead acabou é o motivo escolhido (`encerra`), GRAVADO no lead
 * como `desfechoNegativoEm` na hora de mover. Ler o carimbo, e não a config,
 * é o que impede um clique do gestor em Configurações de reescrever agosto.
 */
export const temDesfechoNegativo = (lead) => paraData(lead?.desfechoNegativoEm) !== null;

/** Leads que já terminaram a história: viraram contrato ou se encerraram. */
export const temDesfecho = (lead) => lead?.etapa === 'fechado' || temDesfechoNegativo(lead);

/**
 * O lead chegou a comparecer?
 *
 * `compareceuEm` é o carimbo gravado desde esta peça, nos dois caminhos que
 * levam a `compareceu`. A RESERVA existe para os leads anteriores, que não têm
 * o campo: um agendamento com status `compareceu` prova o mesmo fato.
 */
export function chegouAComparecer(lead, agendamentos = []) {
  if (paraData(lead?.compareceuEm)) return true;
  return (agendamentos ?? []).some((a) => a?.leadId === lead?.id && a?.status === 'compareceu');
}

/**
 * ★ VI — a coluna "Valor de oportunidade" da lista só faz sentido DEPOIS de
 * compareceu: o orçamento sai da avaliação. Aparece quando há `compareceuEm`
 * (carimbo da VE) OU a etapa já é pós-comparecimento (`compareceu`, `nao_fechou`)
 * OU `fechado` (leads anteriores à VE, sem carimbo). Fora disso a lista mostra
 * "—" e o CSV vai vazio — o valor continua no documento e na ficha.
 */
export const mostraOportunidade = (lead) =>
  Boolean(paraData(lead?.compareceuEm))
  || ETAPAS_POS_COMPARECIMENTO.includes(lead?.etapa)
  || lead?.etapa === 'fechado';

/**
 * O badge cobra o orçamento de quem compareceu e ainda não o registrou — mas
 * não do importado que a equipe ainda não tocou (VO): a mesma regra que cala o
 * "sem retorno" cala a cobrança de orçamento e o "sem negociador".
 */
export const mostrarBadgeOrcamento = (lead) =>
  ETAPAS_POS_COMPARECIMENTO.includes(lead?.etapa)
  && valorOrcadoDe(lead) <= 0
  // ★★ WH: quem foi DECLARADO sem orçamento não é mais cobrado. Sem isto o badge
  // acusaria para sempre quem a própria tela dispensou — e como as três telas
  // (card, filtro e ficha) leem esta função, a condição alcança as três de uma vez.
  && !semOrcamentoDeclarado(lead)
  && !importadoSemAtividade(lead);

/**
 * Conversão de valor: quanto do que foi orçado virou contrato.
 *
 * ⚠ SÓ leads com DESFECHO entram. Um lead em negociação ainda pode fechar;
 * contá-lo no denominador faria a taxa nascer artificialmente baixa e piorar a
 * cada orçamento novo — o oposto do que o número deve dizer.
 *
 * ⚠ NUMERADOR E DENOMINADOR SOBRE O MESMO CONJUNTO: só quem tem desfecho **e**
 * orçamento registrado. Um fechado sem orçamento somaria no numerador sobre um
 * denominador que não o contém, e a taxa passaria de 100 % sem nada ter
 * melhorado. Eles saem dos dois lados e viram `fechadosSemOrcamento` — a
 * contagem que a tela mostra como convite a preencher.
 *
 * @returns `taxa` em %, ou **null** quando não há orçamento nenhum com desfecho.
 *   Null, não 0: "não dá para saber" não é "zero por cento".
 */
export function conversaoDeValor(leads = []) {
  let orcado = 0;
  let fechado = 0;
  let fechadosSemOrcamento = 0;
  /**
   * ★★ WH — quantos compareceram e foram DECLARADOS sem orçamento.
   *
   * ★ Contado pela MARCA, nunca por `valorOrcado === 0`: por zero, o contador
   * devolveria os 222 leads históricos da importação e não diria nada.
   *
   * ★ E ele é contado FORA do laço do desfecho, de propósito: um lead parado em
   * `compareceu` não tem desfecho e nunca entrou nesta conta — nem entra agora. O
   * contador não muda a taxa; ele só torna VISÍVEL o caso legítimo, que hoje sai
   * pelo mesmo `continue` de quem simplesmente esqueceu de preencher.
   */
  let compareceuSemOrcamento = 0;
  for (const lead of leads ?? []) {
    if (semOrcamentoDeclarado(lead) && valorOrcadoDe(lead) <= 0) compareceuSemOrcamento += 1;
  }

  for (const lead of leads ?? []) {
    if (!temDesfecho(lead)) continue;
    const orcamento = valorOrcadoDe(lead);
    if (orcamento <= 0) {
      if (lead.etapa === 'fechado') fechadosSemOrcamento += 1;
      continue;
    }
    orcado += orcamento;
    if (lead.etapa === 'fechado') fechado += Math.max(0, numero(lead.valorFechado));
  }

  return {
    taxa: orcado > 0 ? (fechado / orcado) * 100 : null,
    orcado,
    fechado,
    fechadosSemOrcamento,
    compareceuSemOrcamento,
  };
}

/**
 * Oportunidade perdida: o orçado de quem COMPARECEU e se encerrou sem fechar.
 *
 * Só desfecho negativo (motivo que encerra), e só os que chegaram a comparecer — o orçamento de quem nunca
 * sentou na cadeira não é dinheiro que escapou da mesa.
 */
export function oportunidadePerdida(leads = [], agendamentos = []) {
  let total = 0;
  for (const lead of leads ?? []) {
    if (!temDesfechoNegativo(lead)) continue;
    if (!chegouAComparecer(lead, agendamentos)) continue;
    total += valorOrcadoDe(lead);
  }
  return total;
}

/**
 * Data em que o lead se encerrou — espelho de `dataFechamento`, com reserva.
 * VG: o carimbo é `desfechoNegativoEm`; `perdidoEm` só existe em lead anterior.
 */
export const dataPerda = (lead) =>
  paraData(lead?.desfechoNegativoEm) ?? paraData(lead?.perdidoEm) ?? paraData(lead?.atualizadoEm);
